// ===== Packages =====

import {
    getFunctions,
    httpsCallable,
}                           from 'firebase/functions';

// ===== Firebase =====

import fire                 from '../fire';

// ===== Interfaces =====

import {
    IRecentErrorLog,
}                           from '../interfaces';

// ===== Enums =====

import {
    USER_ACTION_TYPE,
}                           from '../enums';

// Batch action writes to reduce callable invocation volume.
const ACTION_BATCH_FLUSH_INTERVAL_MS = 3000;
const ACTION_BATCH_MAX_SIZE = 40;
const ACTION_BATCH_MAX_ATTEMPTS = 2;
const ACTION_BATCH_BASE_RETRY_DELAY_MS = 200;
const RECENT_ERROR_LOG_RETENTION_MS = 10 * 1000;
const RECENT_ERROR_LOG_MAX_ENTRIES_PER_SESSION = 30;

type QueuedAction = {
    test: boolean,
    actionId: string,
    sessionId: string,
    type: USER_ACTION_TYPE,
    payload?: unknown,
    timestamp: number,
};

const queuedActions: QueuedAction[] = [];
const recentErrorLogsBySession: Map<string, IRecentErrorLog[]> = new Map();
const { auth } = fire();

let flushTimer: number | null = null;
let flushInFlight = false;
let listenersAttached = false;

const now = (): number => Date.now();

const readErrorMessageFromPayload = (payload: unknown): string | undefined => {
    if (
        typeof payload !== 'object'
        || payload == null
        || !('error' in payload)
    ) {
        return undefined;
    }
    const errorValue = (payload as { error?: unknown }).error;
    return typeof errorValue === 'string' && errorValue.length > 0
        ? errorValue
        : undefined;
};

const pruneRecentSessionErrorLogs = (sessionId: string): void => {
    const sessionLogs = recentErrorLogsBySession.get(sessionId);
    if (!sessionLogs || sessionLogs.length === 0) {
        recentErrorLogsBySession.delete(sessionId);
        return;
    }
    const cutoff = now() - RECENT_ERROR_LOG_RETENTION_MS;
    const retainedLogs = sessionLogs.filter((entry) => entry.timestamp >= cutoff);
    if (retainedLogs.length === 0) {
        recentErrorLogsBySession.delete(sessionId);
        return;
    }
    recentErrorLogsBySession.set(sessionId, retainedLogs);
};

const rememberRecentErrorLog = ({
    sessionId,
    message,
}: {
    sessionId: string,
    message: string,
}): void => {
    pruneRecentSessionErrorLogs(sessionId);
    const sessionLogs = recentErrorLogsBySession.get(sessionId) || [];
    sessionLogs.push({
        message,
        timestamp: now(),
    });
    // Keep bounded to avoid unbounded growth per session.
    if (sessionLogs.length > RECENT_ERROR_LOG_MAX_ENTRIES_PER_SESSION) {
        sessionLogs.splice(0, sessionLogs.length - RECENT_ERROR_LOG_MAX_ENTRIES_PER_SESSION);
    }
    recentErrorLogsBySession.set(sessionId, sessionLogs);
};

const relatedErrorMessage = ({
    existing,
    incoming,
}: {
    existing: string,
    incoming: string,
}): boolean => (
    existing === incoming
    || existing.includes(incoming)
    || incoming.includes(existing)
);

export const hasRecentErrorLog = ({
    sessionId,
    message,
    windowMs,
}: {
    sessionId: string,
    message: string,
    windowMs: number,
}): boolean => {
    pruneRecentSessionErrorLogs(sessionId);
    const sessionLogs = recentErrorLogsBySession.get(sessionId) || [];
    if (sessionLogs.length === 0) return false;
    const cutoff = now() - windowMs;
    return sessionLogs.some((entry) => (
        entry.timestamp >= cutoff
        && relatedErrorMessage({
            existing: entry.message,
            incoming: message,
        })
    ));
};

const buildActionId = (): string => {
    if (
        typeof crypto !== 'undefined'
        && typeof crypto.randomUUID === 'function'
    ) {
        return crypto.randomUUID();
    }

    return `action-${now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const sleep = (durationMs: number): Promise<void> => new Promise((resolve) => {
    setTimeout(resolve, durationMs);
});

const clearFlushTimer = (): void => {
    if (flushTimer != null) {
        window.clearTimeout(flushTimer);
        flushTimer = null;
    }
};

const shouldRetry = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false;
    const code = 'code' in error && typeof error.code === 'string'
        ? error.code
        : '';
    return (
        code === 'functions/unavailable'
        || code === 'unavailable'
        || code === 'functions/deadline-exceeded'
        || code === 'deadline-exceeded'
        || code === 'functions/aborted'
        || code === 'aborted'
    );
};

const flushQueuedActions = async ({
    force = false,
}: {
    force?: boolean,
} = {}): Promise<void> => {
    // `force` allows lifecycle events to flush even when below batch size.
    if (flushInFlight || queuedActions.length === 0) return;
    if (!force && queuedActions.length < ACTION_BATCH_MAX_SIZE) return;
    // During sign-out -> anonymous transitions there is a short window
    // with no authenticated Firebase user. Wait for auth to settle.
    if (!auth.currentUser) {
        if (flushTimer == null) {
            flushTimer = window.setTimeout(() => {
                flushQueuedActions({ force: true }).catch(() => null);
            }, ACTION_BATCH_BASE_RETRY_DELAY_MS);
        }
        return;
    }

    flushInFlight = true;
    clearFlushTimer();

    const batch = queuedActions.splice(0, ACTION_BATCH_MAX_SIZE);
    const functions = getFunctions();
    const handleRecordUserActionsBatch = httpsCallable<
        Record<string, unknown>,
        string[]
    >(
        functions,
        'recordUserActionsBatch',
    );

    const attemptFlush = async (attempt: number): Promise<void> => {
        try {
            await handleRecordUserActionsBatch({
                test: batch[0].test,
                actions: batch.map((action) => ({
                    actionId: action.actionId,
                    sessionId: action.sessionId,
                    type: action.type,
                    payload: action.payload,
                    timestamp: action.timestamp,
                })),
            });

            // We always provide actionId client-side, so no ack wiring is required.
            // Keep request awaited so retry/error behavior stays centralized here.
        } catch (error) {
            if (
                attempt >= ACTION_BATCH_MAX_ATTEMPTS
                || !shouldRetry(error)
            ) {
                return;
            }
            // Exponential backoff with light jitter.
            const delay = (
                ACTION_BATCH_BASE_RETRY_DELAY_MS * (2 ** (attempt - 1))
            ) + Math.floor(Math.random() * 100);
            await sleep(delay);
            await attemptFlush(attempt + 1);
        }
    };

    await attemptFlush(1);

    flushInFlight = false;

    if (queuedActions.length > 0) {
        if (queuedActions.length >= ACTION_BATCH_MAX_SIZE) {
            flushQueuedActions({ force: true }).catch(() => null);
        } else if (flushTimer == null) {
            flushTimer = window.setTimeout(() => {
                flushQueuedActions({ force: true }).catch(() => null);
            }, ACTION_BATCH_FLUSH_INTERVAL_MS);
        }
    }
};

const attachFlushLifecycleListeners = (): void => {
    if (
        listenersAttached
        || typeof window === 'undefined'
    ) {
        return;
    }

    listenersAttached = true;

    // Best-effort drain for tab switches/navigation so queued actions are not dropped.
    const flushOnVisibilityHidden = (): void => {
        if (document.visibilityState === 'hidden') {
            flushQueuedActions({ force: true }).catch(() => null);
        }
    };

    window.addEventListener('beforeunload', () => {
        flushQueuedActions({ force: true }).catch(() => null);
    });
    window.addEventListener('pagehide', () => {
        flushQueuedActions({ force: true }).catch(() => null);
    });
    document.addEventListener('visibilitychange', flushOnVisibilityHidden);
};

const recordUserAction = async ({
    test,
    actionId,
    sessionId,
    type,
    payload,
}: {
    test: boolean,
    actionId?: string,
    sessionId: string,
    type: USER_ACTION_TYPE,
    payload?: unknown,
}): Promise<string> => {
    attachFlushLifecycleListeners();

    const payloadErrorMessage = type === USER_ACTION_TYPE.error
        ? readErrorMessageFromPayload(payload)
        : undefined;
    if (payloadErrorMessage) {
        rememberRecentErrorLog({
            sessionId,
            message: payloadErrorMessage,
        });
    }

    const resolvedActionId = actionId || buildActionId();

    queuedActions.push({
        test,
        actionId: resolvedActionId,
        sessionId,
        type,
        payload,
        timestamp: now(),
    });

    if (queuedActions.length >= ACTION_BATCH_MAX_SIZE) {
        flushQueuedActions({ force: true }).catch(() => null);
    } else if (flushTimer == null) {
        flushTimer = window.setTimeout(() => {
            flushQueuedActions({ force: true }).catch(() => null);
        }, ACTION_BATCH_FLUSH_INTERVAL_MS);
    }

    return resolvedActionId;
};

export default recordUserAction;
